import { parseBinaryServerMessage } from "./binary_protocol";

export type ConnectionState = "idle" | "connecting" | "open" | "closed" | "error";

export type ServerConnectionOptions = {
  reconnect?: boolean;
  reconnectDelayMs?: number;
  onStateChange?: (state: ConnectionState) => void;
  onMessage?: (message: unknown) => void;
  onError?: (event: Event) => void;
};

export class ServerConnection {
  private socket: WebSocket | null = null;
  private state: ConnectionState = "idle";
  private manuallyClosed = false;

  public constructor(
    private readonly url: string,
    private readonly options: ServerConnectionOptions = {}
  ) {}

  public connect(): Promise<void> {
    this.close();
    this.manuallyClosed = false;
    this.setState("connecting");

    return new Promise((resolve, reject) => {
      const socket = new WebSocket(this.url);
      socket.binaryType = "arraybuffer";

      this.socket = socket;

      socket.addEventListener("open", () => {
        this.setState("open");
        resolve();
      });

      socket.addEventListener("message", (event) => {
        const parsed = parseMessage(event.data);
        this.options.onMessage?.(parsed);
      });

      socket.addEventListener("error", (event) => {
        this.setState("error");
        this.options.onError?.(event);
        reject(new Error("WebSocket connection failed"));
      });

      socket.addEventListener("close", () => {
        if (this.manuallyClosed) {
          this.setState("idle");
          return;
        }

        this.setState("closed");

        if (this.options.reconnect) {
          const delay = this.options.reconnectDelayMs ?? 1000;

          window.setTimeout(() => {
            void this.connect();
          }, delay);
        }
      });
    });
  }

  public send(message: unknown): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify(message));
  }

  public close(): void {
    this.manuallyClosed = true;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.setState("idle");
  }

  public getState(): ConnectionState {
    return this.state;
  }

  private setState(state: ConnectionState): void {
    this.state = state;
    this.options.onStateChange?.(state);
  }
}

function parseMessage(data: unknown): unknown {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  if (data instanceof ArrayBuffer) {
    try {
      return parseBinaryServerMessage(data);
    } catch {
      return data;
    }
  }

  if (ArrayBuffer.isView(data)) {
    try {
      return parseBinaryServerMessage(copyViewToArrayBuffer(data));
    } catch {
      return data;
    }
  }

  return data;
}

function copyViewToArrayBuffer(data: ArrayBufferView): ArrayBuffer {
  const output = new ArrayBuffer(data.byteLength);
  const outputView = new Uint8Array(output);
  const inputView = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);

  outputView.set(inputView);

  return output;
}